<script lang="ts">
	// Trash (SAY-1463). A collapsible sidebar section listing soft-deleted chats,
	// each with Restore and "Delete forever". Trashing happens from SessionList
	// (reversible, no modal); PERMANENT deletion is gated behind a confirm here
	// because it drops the session, its history, and its worktree for good.
	import * as Collapsible from '$lib/components/ui/collapsible/index.js';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import { Button } from '$lib/components/ui/button/index.js';
	import Trash2Icon from '@lucide/svelte/icons/trash-2';
	import RotateCcwIcon from '@lucide/svelte/icons/rotate-ccw';
	import LoaderCircleIcon from '@lucide/svelte/icons/loader-circle';
	import {
		getSessions,
		markRestored,
		markTrashed,
		removeSession,
		type SessionRow,
	} from '$lib/sessions-store.svelte';
	import { restoreSession, deleteSession } from '$lib/api';
	import { toast } from 'svelte-sonner';

	const sessions = getSessions();

	// Trashed chats, most-recently-trashed first.
	const trashed = $derived(
		sessions
			.filter((s) => s.deletedAt)
			.sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)),
	);

	let restoring = $state<string | null>(null);
	let pendingDelete = $state<SessionRow | null>(null);
	let deleting = $state(false);

	function label(row: SessionRow): string {
		return row.name ?? row.sessionId.slice(0, 8);
	}

	async function restoreOne(row: SessionRow): Promise<void> {
		if (restoring) return;
		restoring = row.sessionId;
		markRestored(row.sessionId); // optimistic — row leaves the Trash at once
		try {
			await restoreSession(row.sessionId);
			toast.success('Restored', { description: label(row) });
		} catch (err) {
			markTrashed(row.sessionId); // put it back on failure
			toast.error('Restore failed', {
				description: err instanceof Error ? err.message : String(err),
			});
		} finally {
			restoring = null;
		}
	}

	async function confirmDelete(): Promise<void> {
		const row = pendingDelete;
		if (!row || deleting) return;
		deleting = true;
		try {
			await deleteSession(row.sessionId);
			removeSession(row.sessionId);
			pendingDelete = null;
			toast.success('Deleted permanently', { description: label(row) });
		} catch (err) {
			toast.error('Delete failed', {
				description: err instanceof Error ? err.message : 'Server did not delete the chat.',
			});
		} finally {
			deleting = false;
		}
	}
</script>

{#if trashed.length > 0}
	<Collapsible.Root class="px-0">
		<Collapsible.Trigger
			class="group/trash mt-2 flex w-full items-center gap-1.5 px-3.5 pt-1 pb-1 text-left text-[10.5px] font-medium uppercase tracking-wider text-[color:var(--fg-faint)] transition-colors hover:text-sidebar-foreground focus-visible:outline-none"
		>
			<Trash2Icon class="size-3" aria-hidden="true" />
			<span class="flex-1">Trash ({trashed.length})</span>
			<span
				class="text-[10px] transition-transform group-data-[state=open]/trash:rotate-90"
				aria-hidden="true">›</span
			>
		</Collapsible.Trigger>
		<Collapsible.Content>
			<ul class="flex flex-col gap-px px-2">
				{#each trashed as s (s.sessionId)}
					<li class="group relative flex items-center gap-1 rounded-md px-2.5 py-[7px] hover:bg-[color:var(--surface-2,var(--secondary))]">
						<span class="min-w-0 flex-1 truncate text-[12.5px] text-[color:var(--fg-faint)]">
							{label(s)}
						</span>
						<div class="flex shrink-0 gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
							<button
								type="button"
								class="rounded p-3 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground sm:p-1"
								aria-label="Restore chat"
								title="Restore"
								disabled={restoring === s.sessionId}
								onclick={() => void restoreOne(s)}
							>
								{#if restoring === s.sessionId}
									<LoaderCircleIcon class="size-3.5 animate-spin" />
								{:else}
									<RotateCcwIcon class="size-3.5" />
								{/if}
							</button>
							<button
								type="button"
								class="rounded p-3 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive sm:p-1"
								aria-label="Delete forever"
								title="Delete forever"
								onclick={() => (pendingDelete = s)}
							>
								<Trash2Icon class="size-3.5" />
							</button>
						</div>
					</li>
				{/each}
			</ul>
		</Collapsible.Content>
	</Collapsible.Root>
{/if}

<!-- Permanent deletion (SAY-1463). Irreversible: drops the session, its history,
     and its worktree, so it's gated behind an explicit confirm. -->
<Dialog.Root
	open={pendingDelete !== null}
	onOpenChange={(v) => {
		if (!v && !deleting) pendingDelete = null;
	}}
>
	<Dialog.Content class="sm:max-w-md" showCloseButton={!deleting}>
		<Dialog.Header>
			<Dialog.Title class="flex items-center gap-2 text-[16px] font-semibold tracking-[-0.01em]">
				<Trash2Icon class="size-4 text-destructive" />
				Delete forever
			</Dialog.Title>
			<Dialog.Description class="text-[12.5px] text-muted-foreground">
				Permanently delete <span class="font-medium text-foreground"
					>“{pendingDelete ? label(pendingDelete) : ''}”</span
				>? This removes the conversation, its history, and any files it created.
				This can't be undone.
			</Dialog.Description>
		</Dialog.Header>
		<Dialog.Footer class="gap-2">
			<Button variant="outline" onclick={() => (pendingDelete = null)} disabled={deleting}>
				Cancel
			</Button>
			<Button variant="destructive" onclick={confirmDelete} disabled={deleting}>
				{#if deleting}
					<LoaderCircleIcon class="size-3.5 animate-spin" />
				{/if}
				Delete forever
			</Button>
		</Dialog.Footer>
	</Dialog.Content>
</Dialog.Root>
